import { useEffect, useCallback } from 'react';
import { InteractionManager } from 'react-native';
import { differenceInMinutes, isBefore, startOfDay, format, subYears, } from 'date-fns';
import { useFetchOld } from 'hooks';
import { getBalanceHistory } from 'actions';
import { getPotBalanceHistory, getPortfolioBalanceHistory, getALLBalanceHistory, } from 'actions/balanceHistory';
import { assertUnreachable } from 'utils/types';
import { useAppSelector, useAppStore } from 'store/hooks';
import { selectSelectedSpace } from 'features/spaces/reducers/selectors';
const FETCH_PARAMS = {
    muteError: true,
    muteResult: true,
    muteFetching: true,
};
const shouldMakeRequest = (lastApiCallTimeStamp, dateState) => {
    if (!lastApiCallTimeStamp) {
        return true;
    }
    if (differenceInMinutes(dateState, new Date(lastApiCallTimeStamp)) > 10 ||
        isBefore(startOfDay(new Date(lastApiCallTimeStamp)), startOfDay(dateState))) {
        return true;
    }
    return false;
};
export const useBalanceHistory = (title) => {
    const store = useAppStore();
    const spaceId = useAppSelector(selectSelectedSpace);
    // Changes when a finished sync makes the cached history stale
    const syncInvalidationCount = useAppSelector((state) => state.balanceHistory.syncInvalidationCount);
    const [, , , fetch] = useFetchOld(FETCH_PARAMS);
    const getCheckingBalance = useCallback((lastYear, toNow) => {
        const state = store.getState();
        const dateState = new Date();
        if (!state.balanceHistory.checkingBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.checkingLastFetchedTimeStamp, dateState)) {
            fetch(getBalanceHistory(lastYear, toNow, 'CHECKING', '1day'));
        }
        if (!state.balanceHistory.everydayAllBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.everydayAllLastFetchedTimeStamp, dateState)) {
            fetch(getALLBalanceHistory('EVERYDAY'));
        }
    }, [spaceId]);
    const getSavingsBalance = useCallback((lastYear, toNow) => {
        const state = store.getState();
        const dateState = new Date();
        if (!state.balanceHistory.savingsBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.savingsLastFetchedTimeStamp, dateState)) {
            fetch(getBalanceHistory(lastYear, toNow, 'SAVINGS', '1day'));
        }
        if (spaceId &&
            !state.balanceHistory.potsBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.potsLastFetchedTimeStamp, dateState)) {
            fetch(getPotBalanceHistory(lastYear, toNow, spaceId));
        }
        if (!state.balanceHistory.savingsAllBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.savingsAllLastFetchedTimeStamp, dateState)) {
            fetch(getALLBalanceHistory('SAVINGS'));
        }
    }, [spaceId]);
    const getInvestmentBalance = useCallback((lastYear, toNow) => {
        const state = store.getState();
        const dateState = new Date();
        if (!state.balanceHistory.investmentBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.investmentLastFetchedTimeStamp, dateState)) {
            fetch(getBalanceHistory(lastYear, toNow, 'INVESTMENT', '1day'));
        }
        if (spaceId &&
            !state.balanceHistory.portfolioBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.portfolioLastFetchedTimeStamp, dateState)) {
            fetch(getPortfolioBalanceHistory(lastYear, toNow, spaceId));
        }
        if (!state.balanceHistory.investmentAllBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.investmentAllLastFetchedTimeStamp, dateState)) {
            fetch(getALLBalanceHistory('INVESTMENT'));
        }
    }, [spaceId]);
    const getNetworthDebtBalance = useCallback((lastYear, toNow) => {
        const state = store.getState();
        const dateState = new Date();
        if (!state.balanceHistory.debtBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.debtLastFetchedTimeStamp, dateState)) {
            fetch(getBalanceHistory(lastYear, toNow, 'DEBT', '1day'));
        }
        if (!state.balanceHistory.debtAllBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.debtAllLastFetchedTimeStamp, dateState)) {
            fetch(getALLBalanceHistory('NET_WORTH', 'DEBT'));
        }
    }, []);
    const getNetworthAssetsBalance = useCallback((lastYear, toNow) => {
        const state = store.getState();
        const dateState = new Date();
        if (!state.balanceHistory.assetsBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.assetsLastFetchedTimeStamp, dateState)) {
            fetch(getBalanceHistory(lastYear, toNow, 'ASSETS', '1day'));
        }
        if (spaceId &&
            !state.balanceHistory.potsBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.potsLastFetchedTimeStamp, dateState)) {
            fetch(getPotBalanceHistory(lastYear, toNow, spaceId));
        }
        if (spaceId &&
            !state.balanceHistory.portfolioBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.portfolioLastFetchedTimeStamp, dateState)) {
            fetch(getPortfolioBalanceHistory(lastYear, toNow, spaceId));
        }
        if (!state.balanceHistory.assetAllBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.assetAllLastFetchedTimeStamp, dateState)) {
            fetch(getALLBalanceHistory('NET_WORTH', 'ASSET'));
        }
    }, [spaceId]);
    const getNetworthBalance = useCallback((lastYear, toNow) => {
        const state = store.getState();
        const dateState = new Date();
        if (!state.balanceHistory.debtAllBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.debtAllLastFetchedTimeStamp, dateState)) {
            fetch(getALLBalanceHistory('NET_WORTH', 'DEBT'));
        }
        if (!state.balanceHistory.assetAllBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.assetAllLastFetchedTimeStamp, dateState)) {
            fetch(getALLBalanceHistory('NET_WORTH', 'ASSET'));
        }
        if (!state.balanceHistory.networthAllBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.networthAllLastFetchedTimeStamp, dateState)) {
            fetch(getALLBalanceHistory('NET_WORTH'));
        }
        if (!state.balanceHistory.networthBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.networthLastFetchedTimeStamp, dateState)) {
            fetch(getBalanceHistory(lastYear, toNow, 'NETWORTH', '1day'));
        }
        if (spaceId &&
            !state.balanceHistory.potsBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.potsLastFetchedTimeStamp, dateState)) {
            fetch(getPotBalanceHistory(lastYear, toNow, spaceId));
        }
        if (spaceId &&
            !state.balanceHistory.portfolioBalanceHistoryReq &&
            shouldMakeRequest(state.balanceHistory.portfolioLastFetchedTimeStamp, dateState)) {
            fetch(getPortfolioBalanceHistory(lastYear, toNow, spaceId));
        }
    }, [spaceId]);
    useEffect(() => {
        InteractionManager.runAfterInteractions(() => {
            const toNow = format(new Date(), 'yyyy-MM-dd');
            const lastYear = format(subYears(new Date(), 1), 'yyyy-MM-dd');
            switch (title) {
                case 'Everyday': {
                    getCheckingBalance(lastYear, toNow);
                    break;
                }
                case 'Investments': {
                    getInvestmentBalance(lastYear, toNow);
                    break;
                }
                case 'Savings': {
                    getSavingsBalance(lastYear, toNow);
                    break;
                }
                case 'Net Worth - Main': {
                    getNetworthBalance(lastYear, toNow);
                    getNetworthDebtBalance(lastYear, toNow);
                    getNetworthAssetsBalance(lastYear, toNow);
                    break;
                }
                case 'Net Worth - Debt': {
                    getNetworthDebtBalance(lastYear, toNow);
                    break;
                }
                case 'Net Worth - Asset': {
                    getNetworthAssetsBalance(lastYear, toNow);
                    break;
                }
                default: {
                    assertUnreachable(title);
                    break;
                }
            }
        });
    }, [
        title,
        spaceId,
        syncInvalidationCount,
        getCheckingBalance,
        getInvestmentBalance,
        getSavingsBalance,
        getNetworthDebtBalance,
        getNetworthAssetsBalance,
        getNetworthBalance,
    ]);
};
